// lib/qqq/driver-suggest.js
// ═══════════════════════════════════════════════════════════
// DRIVER SUGGEST — auto-propose move drivers from captured data
// ═══════════════════════════════════════════════════════════
// Per Tim's spec: Shift behaves like Jarvis. It does the work
// of scanning the day's captured data (forces, candle, prices,
// gamma, events, international markets, etc.) and PROPOSES
// likely move drivers. Tim confirms/edits/adds via one-click.
//
// Pure module — no DB, no fetchers. Caller passes in the live
// session data (data.prices, data.environment, data.gamma,
// data.events, data.openingCandle, data.windowOutcome) and gets
// back an array of suggested drivers ready for the user to
// accept into move_drivers.
//
// Each suggestion uses the canonical 9-category taxonomy:
//   pre_market / international / macro / earnings / news /
//   dealer_flow / market_structure / sentiment / technical
// ═══════════════════════════════════════════════════════════

const MEGA_CAP_TICKERS = ['AAPL', 'MSFT', 'NVDA', 'AMZN', 'META', 'GOOGL', 'GOOG', 'TSLA', 'AVGO'];
const FOMC_KEYWORDS    = /fomc|fed\s*meeting|rate decision|powell|fed minutes|jackson hole/i;
const ECON_KEYWORDS    = /\b(cpi|ppi|nfp|jobs report|payrolls|gdp|inflation|unemployment|jolts|claims|retail sales|ism|pmi)\b/i;

/**
 * Top-level — return an array of suggested drivers.
 * Each suggestion: { category, driver, impact, directional, source, autoSuggested: true }
 */
export function suggestDrivers(sessionData) {
  const out = [];
  out.push(...suggestPreMarket(sessionData));
  out.push(...suggestInternational(sessionData));
  out.push(...suggestMacro(sessionData));
  out.push(...suggestEarnings(sessionData));
  out.push(...suggestDealerFlow(sessionData));
  out.push(...suggestMarketStructure(sessionData));
  out.push(...suggestSentiment(sessionData));
  out.push(...suggestTechnical(sessionData));
  // Commit 32 — Massive daily indicators (RSI / SMA / momentum)
  out.push(...suggestFromDailyIndicators(sessionData));
  // Sort by impact desc so the most-actionable suggestions surface first
  return out.sort((a, b) => impactRank(b.impact) - impactRank(a.impact));
}

// ─── Per-category suggesters ───────────────────────────────

function suggestPreMarket(sd) {
  const out = [];
  const qqq = sd?.prices?.qqq || {};
  const spy = sd?.prices?.spy || {};
  const qqqChg = parseLooseNum(qqq.change);
  const spyChg = parseLooseNum(spy.change);

  if (qqqChg != null && Math.abs(qqqChg) >= 0.30) {
    const dir = qqqChg > 0 ? 'up' : 'down';
    out.push({
      category:    'pre_market',
      driver:      `QQQ pre-market ${dir === 'up' ? 'gap up' : 'gap down'} ${qqqChg >= 0 ? '+' : ''}${qqqChg.toFixed(2)}%`,
      impact:      Math.abs(qqqChg) >= 0.75 ? 'high' : 'medium',
      directional: dir,
      source:      'pre-market QQQ change',
    });
  }
  if (spyChg != null && Math.abs(spyChg) >= 0.30 && (qqqChg == null || Math.abs(spyChg - qqqChg) >= 0.20)) {
    const dir = spyChg > 0 ? 'up' : 'down';
    out.push({
      category:    'pre_market',
      driver:      `SPY pre-market ${dir === 'up' ? 'gap up' : 'gap down'} ${spyChg >= 0 ? '+' : ''}${spyChg.toFixed(2)}% (vs QQQ)`,
      impact:      'medium',
      directional: dir,
      source:      'pre-market SPY change',
    });
  }
  return out;
}

function suggestInternational(sd) {
  const out = [];
  // International futures already captured in environment.futures
  // (e.g., DAX), and we have specific indices in the broader corpus.
  const futures = Array.isArray(sd?.environment?.futures) ? sd.environment.futures : [];
  for (const f of futures) {
    const v = parseLooseNum(f.value);
    if (v == null || Math.abs(v) < 0.50) continue;
    if (!['DAX', 'NQ', 'ES'].includes(String(f.label).replace(/\s.*$/, ''))) continue;
    const dir = v > 0 ? 'up' : 'down';
    out.push({
      category:    'international',
      driver:      `${f.label} ${dir === 'up' ? '+' : ''}${v.toFixed(2)}% overnight`,
      impact:      Math.abs(v) >= 1.0 ? 'high' : 'medium',
      directional: dir,
      source:      `${f.label} overnight`,
    });
  }
  // Dollar (DXY) — captured in environment.futures usually as DXY
  const dxy = futures.find((f) => /DXY/i.test(f.label));
  if (dxy) {
    const dxyChg = parseLooseNum(dxy.value);
    if (dxyChg != null && Math.abs(dxyChg) >= 0.30) {
      // Note: dollar UP is bearish for tech (multinational revenue), so flip directional
      const dir = dxyChg > 0 ? 'down' : 'up';  // inverted for QQQ impact
      out.push({
        category:    'international',
        driver:      `Dollar ${dxyChg > 0 ? 'strong' : 'weak'} (DXY ${dxyChg >= 0 ? '+' : ''}${dxyChg.toFixed(2)}%) — ${dir === 'up' ? 'tailwind' : 'headwind'} for tech`,
        impact:      Math.abs(dxyChg) >= 0.50 ? 'high' : 'medium',
        directional: dir,
        source:      'DXY change',
      });
    }
  }
  return out;
}

function suggestMacro(sd) {
  const out = [];
  const events = Array.isArray(sd?.risk) ? sd.risk : sd?.events || [];

  // FOMC events
  for (const e of events) {
    if (!e?.name) continue;
    if (FOMC_KEYWORDS.test(e.name)) {
      const inWindow = isTimeInTradingWindow(e.time);
      out.push({
        category:    'macro',
        driver:      `${e.name}${e.time ? ` at ${e.time}` : ''}${inWindow ? ' (IN window)' : ' (outside window)'}`,
        impact:      inWindow ? 'high' : 'medium',
        directional: 'neutral',
        source:      'event calendar',
      });
    } else if (ECON_KEYWORDS.test(e.name)) {
      const inWindow = isTimeInTradingWindow(e.time);
      out.push({
        category:    'macro',
        driver:      `${e.name}${e.time ? ` at ${e.time}` : ''}${inWindow ? ' (IN window)' : ''}`,
        impact:      e.sev === 'hi' ? 'high' : e.sev === 'md' ? 'medium' : 'low',
        directional: 'neutral',
        source:      'event calendar',
      });
    }
  }

  // 10Y yield from environment yieldCurve
  const yc = Array.isArray(sd?.environment?.yieldCurve) ? sd.environment.yieldCurve : [];
  const tenY = yc.find((r) => r.label === '10Y');
  // We don't have intraday change, but if curve is inverted that's a macro driver
  const spread = parseLooseNum(sd?.environment?._raw?.spread10y2y);
  if (spread != null && spread < 0) {
    out.push({
      category:    'macro',
      driver:      `Yield curve inverted (10Y−2Y at ${spread.toFixed(2)}%)`,
      impact:      'medium',
      directional: 'down',
      source:      '10Y-2Y spread',
    });
  }
  return out;
}

function suggestEarnings(sd) {
  const out = [];
  const events = Array.isArray(sd?.risk) ? sd.risk : sd?.events || [];
  for (const e of events) {
    if (!e?.name) continue;
    const upperName = String(e.name).toUpperCase();
    if (MEGA_CAP_TICKERS.some((t) => upperName.includes(t)) && /earn|report/i.test(e.name)) {
      const ticker = MEGA_CAP_TICKERS.find((t) => upperName.includes(t));
      out.push({
        category:    'earnings',
        driver:      `${ticker} earnings${e.time ? ` ${e.time}` : ''}`,
        impact:      'high',  // mega-cap earnings = high impact regardless
        directional: 'neutral',
        source:      'event calendar',
      });
    }
  }
  return out;
}

function suggestDealerFlow(sd) {
  const out = [];
  const g = sd?.gamma || sd?.structure?.gamma || {};
  const flip = parseLooseNum(g.flip);
  const callWall = parseLooseNum(g.callWall);
  const putWall = parseLooseNum(g.putWall);
  const qqqPrice = parseLooseNum(sd?.prices?.qqq?.price) ?? parseLooseNum(sd?.prices?.levels?.qqqNow);

  if (qqqPrice != null && flip != null) {
    const distance = qqqPrice - flip;
    if (Math.abs(distance) >= 1.0) {
      const above = distance > 0;
      out.push({
        category:    'dealer_flow',
        driver:      `QQQ ${above ? 'above' : 'below'} gamma flip (${flip.toFixed(2)}) by ${Math.abs(distance).toFixed(2)} — dealers ${above ? 'suppress' : 'amplify'}`,
        impact:      Math.abs(distance) >= 3.0 ? 'high' : 'medium',
        directional: above ? 'up' : 'down',
        source:      'gamma flip distance',
      });
    }
  }
  if (qqqPrice != null && callWall != null && Math.abs(qqqPrice - callWall) <= 1.0) {
    out.push({
      category:    'dealer_flow',
      driver:      `QQQ near call wall (${callWall.toFixed(2)}) — magnet/ceiling`,
      impact:      'high',
      directional: 'neutral',
      source:      'call wall proximity',
    });
  }
  if (qqqPrice != null && putWall != null && Math.abs(qqqPrice - putWall) <= 1.0) {
    out.push({
      category:    'dealer_flow',
      driver:      `QQQ near put wall (${putWall.toFixed(2)}) — magnet/floor`,
      impact:      'high',
      directional: 'neutral',
      source:      'put wall proximity',
    });
  }
  return out;
}

function suggestMarketStructure(sd) {
  const out = [];
  // Tech leading SPY — from environment._raw or sectors
  const techLead = parseLooseNum(sd?.flow?._raw?.techLeadingSpy);
  if (techLead != null && Math.abs(techLead) >= 0.30) {
    const dir = techLead > 0 ? 'up' : 'down';
    out.push({
      category:    'market_structure',
      driver:      `Tech ${dir === 'up' ? 'leading' : 'lagging'} SPY by ${techLead >= 0 ? '+' : ''}${techLead.toFixed(2)}pp`,
      impact:      Math.abs(techLead) >= 0.75 ? 'high' : 'medium',
      directional: dir,
      source:      'sector rotation',
    });
  }
  // Defensive sector strength (XLP, XLU)
  const sectors = Array.isArray(sd?.flow?.sectors) ? sd.flow.sectors : [];
  const defensives = sectors.filter((s) =>
    /Consumer Staples|Utilities|XLP|XLU/i.test(s.name)
  );
  for (const s of defensives) {
    const v = parseLooseNum(s.value);
    if (v != null && v >= 0.50) {
      out.push({
        category:    'market_structure',
        driver:      `${s.name} leading (+${v.toFixed(2)}%) — risk-off rotation`,
        impact:      'medium',
        directional: 'down',
        source:      'defensive sector strength',
      });
    }
  }
  return out;
}

function suggestSentiment(sd) {
  const out = [];
  const vixTerm = Array.isArray(sd?.environment?.vixTerm) ? sd.environment.vixTerm : [];
  const vix = vixTerm.find((r) => r.label === '30-Day');
  const vix9 = vixTerm.find((r) => r.label === '9-Day');
  const vixVal  = parseLooseNum(vix?.raw)  ?? parseLooseNum(vix?.value);
  const vix9Val = parseLooseNum(vix9?.raw) ?? parseLooseNum(vix9?.value);

  if (vixVal != null && vixVal >= 22) {
    out.push({
      category:    'sentiment',
      driver:      `VIX elevated at ${vixVal.toFixed(1)} — stressed regime`,
      impact:      vixVal >= 28 ? 'high' : 'medium',
      directional: 'down',
      source:      '30-Day VIX',
    });
  }
  if (vixVal != null && vix9Val != null && vix9Val > vixVal + 0.3) {
    out.push({
      category:    'sentiment',
      driver:      `VIX backwardation (9D ${vix9Val.toFixed(1)} > 30D ${vixVal.toFixed(1)}) — acute fear`,
      impact:      'high',
      directional: 'down',
      source:      'VIX term structure',
    });
  }
  if (vixVal != null && vix9Val != null && vix9Val < vixVal - 0.3) {
    out.push({
      category:    'sentiment',
      driver:      `VIX contango (9D ${vix9Val.toFixed(1)} < 30D ${vixVal.toFixed(1)}) — fear resolving`,
      impact:      'medium',
      directional: 'up',
      source:      'VIX term structure',
    });
  }

  // PCR
  const pcr = parseLooseNum(sd?.pcr?.value) ?? parseLooseNum(sd?.flow?.pcr?.value);
  if (pcr != null) {
    if (pcr < 0.85) {
      out.push({
        category:    'sentiment',
        driver:      `PCR ${pcr.toFixed(2)} — call buying dominant`,
        impact:      'medium',
        directional: 'up',
        source:      'put/call ratio',
      });
    } else if (pcr > 1.05) {
      out.push({
        category:    'sentiment',
        driver:      `PCR ${pcr.toFixed(2)} — put-heavy positioning`,
        impact:      'medium',
        directional: 'down',
        source:      'put/call ratio',
      });
    }
  }
  return out;
}

function suggestTechnical(sd) {
  const out = [];
  const lvl = sd?.prices?.levels || {};
  const trend = sd?.prices?.trend || {};
  const qqqNow = parseLooseNum(lvl.qqqNow) ?? parseLooseNum(sd?.prices?.qqq?.price);
  const pdh = parseLooseNum(lvl.pdh);
  const pdl = parseLooseNum(lvl.pdl);
  const gapFlip = parseLooseNum(lvl.gapFlip);

  // PDH break
  if (qqqNow != null && pdh != null && qqqNow > pdh) {
    out.push({
      category:    'technical',
      driver:      `QQQ above PDH (${pdh.toFixed(2)}) — breakout structure`,
      impact:      'high',
      directional: 'up',
      source:      'QQQ vs PDH',
    });
  }
  // PDL break
  if (qqqNow != null && pdl != null && qqqNow < pdl) {
    out.push({
      category:    'technical',
      driver:      `QQQ below PDL (${pdl.toFixed(2)}) — breakdown structure`,
      impact:      'high',
      directional: 'down',
      source:      'QQQ vs PDL',
    });
  }
  // At gap flip
  if (qqqNow != null && gapFlip != null && Math.abs(qqqNow - gapFlip) <= 0.30) {
    out.push({
      category:    'technical',
      driver:      `QQQ at gap flip (${gapFlip.toFixed(2)}) — decision point`,
      impact:      'medium',
      directional: 'neutral',
      source:      'gap flip proximity',
    });
  }
  // Stack reading
  const above = [trend.vs20d, trend.vs50d, trend.vs200d].filter((v) => v === 'above').length;
  const below = [trend.vs20d, trend.vs50d, trend.vs200d].filter((v) => v === 'below').length;
  if (above === 3) {
    out.push({
      category:    'technical',
      driver:      'QQQ above 20D + 50D + 200D — fully stacked uptrend',
      impact:      'medium',
      directional: 'up',
      source:      'MA stack',
    });
  } else if (below === 3) {
    out.push({
      category:    'technical',
      driver:      'QQQ below 20D + 50D + 200D — stacked downtrend',
      impact:      'medium',
      directional: 'down',
      source:      'MA stack',
    });
  }
  return out;
}

// ─── Daily Indicators (Commit 32 — Massive: RSI/SMA/momentum) ──
// Reads sd.dailyIndicators (populated from market_structure.
// daily_indicators by intelligence-massive-daily-ohlc cron at
// 4:30 PM ET). Generates 4 driver families:
//   - RSI extremes (>70 overbought, <30 oversold)
//   - Price extension vs SMA 50 (>5% above/below = stretched)
//   - Multi-day momentum streaks (3+ days same direction)
//   - 5-day move magnitude (>3% = trend continuation signal)
function suggestFromDailyIndicators(sd) {
  const out = [];
  const di = sd?.dailyIndicators;
  if (!di) return out;

  // ─── RSI extremes ───
  const rsi = Number(di.rsi_14);
  if (Number.isFinite(rsi)) {
    if (rsi >= 75) {
      out.push({
        category:    'sentiment',
        driver:      `QQQ RSI ${rsi.toFixed(1)} — heavily overbought (likely fade pressure)`,
        impact:      rsi >= 80 ? 'high' : 'medium',
        directional: 'down',
        source:      'massive_rsi_14',
      });
    } else if (rsi <= 25) {
      out.push({
        category:    'sentiment',
        driver:      `QQQ RSI ${rsi.toFixed(1)} — heavily oversold (likely bounce setup)`,
        impact:      rsi <= 20 ? 'high' : 'medium',
        directional: 'up',
        source:      'massive_rsi_14',
      });
    } else if (rsi >= 70) {
      out.push({
        category:    'sentiment',
        driver:      `QQQ RSI ${rsi.toFixed(1)} — overbought territory`,
        impact:      'medium',
        directional: 'down',
        source:      'massive_rsi_14',
      });
    } else if (rsi <= 30) {
      out.push({
        category:    'sentiment',
        driver:      `QQQ RSI ${rsi.toFixed(1)} — oversold territory`,
        impact:      'medium',
        directional: 'up',
        source:      'massive_rsi_14',
      });
    }
  }

  // ─── Price extension vs SMA 50 ───
  const pctSma50 = Number(di.pct_above_sma_50);
  if (Number.isFinite(pctSma50)) {
    if (pctSma50 >= 5.0) {
      out.push({
        category:    'technical',
        driver:      `QQQ extended +${pctSma50.toFixed(2)}% above 50D MA — stretched`,
        impact:      pctSma50 >= 8.0 ? 'high' : 'medium',
        directional: 'down',
        source:      'massive_sma_50',
      });
    } else if (pctSma50 <= -5.0) {
      out.push({
        category:    'technical',
        driver:      `QQQ extended ${pctSma50.toFixed(2)}% below 50D MA — stretched down`,
        impact:      pctSma50 <= -8.0 ? 'high' : 'medium',
        directional: 'up',
        source:      'massive_sma_50',
      });
    }
  }

  // ─── Multi-day streak ───
  const upStreak   = Number(di.consecutive_up_days)   || 0;
  const downStreak = Number(di.consecutive_down_days) || 0;
  if (upStreak >= 4) {
    out.push({
      category:    'technical',
      driver:      `QQQ ${upStreak} up days in a row — momentum context`,
      impact:      upStreak >= 6 ? 'high' : 'medium',
      directional: upStreak >= 6 ? 'down' : 'up',  // 6+ = exhaustion risk
      source:      'massive_streak_up',
    });
  } else if (downStreak >= 4) {
    out.push({
      category:    'technical',
      driver:      `QQQ ${downStreak} down days in a row — oversold context`,
      impact:      downStreak >= 6 ? 'high' : 'medium',
      directional: downStreak >= 6 ? 'up' : 'down',  // 6+ = exhaustion / bounce
      source:      'massive_streak_down',
    });
  }

  // ─── 5-day move magnitude ───
  const fiveDay = Number(di.five_day_pct);
  if (Number.isFinite(fiveDay) && Math.abs(fiveDay) >= 3.0) {
    const dir = fiveDay > 0 ? 'up' : 'down';
    out.push({
      category:    'technical',
      driver:      `QQQ ${fiveDay >= 0 ? '+' : ''}${fiveDay.toFixed(2)}% over last 5 sessions`,
      impact:      Math.abs(fiveDay) >= 5.0 ? 'high' : 'medium',
      directional: dir,
      source:      'massive_5day_move',
    });
  }

  return out;
}

// ─── Helpers ────────────────────────────────────────────────

function parseLooseNum(v) {
  if (v == null || v === '—') return null;
  const cleaned = String(v).replace(/[^0-9.\-]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function impactRank(impact) {
  return { high: 3, medium: 2, low: 1 }[impact] || 0;
}

function isTimeInTradingWindow(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return false;
  const m = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return false;
  let hh = Number(m[1]);
  const mm = Number(m[2]);
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && hh !== 12) hh += 12;
  if (ampm === 'AM' && hh === 12) hh = 0;
  const minOfDay = hh * 60 + mm;
  return minOfDay >= 9 * 60 + 30 && minOfDay <= 11 * 60 + 30;
}
